/**
 * The mandatory Teams "configuration" page for `configurableTabs`
 * (manifest/manifest.json). Teams requires one of these to exist before it
 * will let anyone add the app to a meeting — without it, the Save button in
 * the "add to meeting" dialog never enables, which is why installing the
 * app failed silently before this file existed.
 *
 * There is nothing to configure. The two settings this app remembers
 * (seconds per person, grace period) are edited on the tick list itself,
 * once the standup is open — not here, and not per-meeting-instance. So
 * this page's only job is the Teams handshake: tell the host the
 * configuration is valid immediately, and on save, tell it what URL to
 * actually load for this tab.
 *
 * Loaded via the same bundle as the main app, selected by a query
 * parameter (`?config=1`) rather than a separate route — the same
 * convention `src/roster/auth.ts` uses for the sign-in popup, chosen so a
 * plain static host (no server-side rewrites) can serve it.
 */
import { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { Spinner, Text, tokens } from "@fluentui/react-components";

/**
 * The URL Teams should actually load for this tab: this same deployment,
 * with the `?config=1` marker stripped so the tab opens the standup app
 * and not the configuration page again.
 */
function contentUrl(): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function ConfigPage() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function setup() {
      try {
        await microsoftTeams.app.initialize();
      } catch {
        // Being loaded outside Teams (e.g. directly in a browser tab while
        // developing) — nothing to configure against, but don't block
        // rendering over it.
      }

      // Nothing on this page needs filling in, so it is valid the moment
      // Teams asks — this is what enables the Save button.
      microsoftTeams.pages.config.setValidityState(true);

      microsoftTeams.pages.config.registerOnSaveHandler((saveEvent) => {
        microsoftTeams.pages.config
          .setConfig({
            contentUrl: contentUrl(),
            websiteUrl: contentUrl(),
            suggestedDisplayName: "Standup Timer",
          })
          .then(() => saveEvent.notifySuccess())
          .catch(() => saveEvent.notifyFailure("Could not save the configuration."));
      });

      if (!cancelled) setReady(true);
    }

    void setup();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: tokens.spacingVerticalM,
        height: "100vh",
        textAlign: "center",
        padding: tokens.spacingHorizontalL,
      }}
    >
      {ready ? (
        <>
          <Text weight="semibold" size={400}>
            Standup Timer
          </Text>
          <Text>
            Nothing to set up here — press <strong>Save</strong> to add it. Seconds
            per person and the grace period are set from inside the app itself, once
            the standup is open.
          </Text>
        </>
      ) : (
        <Spinner label="Loading…" />
      )}
    </div>
  );
}
